import SettlementReportingPeriodSelect from './SettlementReportingPeriodSelect';
import { Box, Grid, inputLabelClasses, ListItemText, MenuItem, Paper, TextField } from "@mui/material";
import { DateTime } from "luxon";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CommissionTypes, DateRangeReportFilter, IdReportFilter, LotStatuses, RecencyReportFilter, ReportFilterTargetFields, ReportTemplateTypes } from "../../api/dtos";
import ProjectsContext from "../../contexts/data/ProjectsContext";
import useMinExchangeDateByProjectId from "../../hooks/data/useMinExchangeDateByProjectId";
import StageMultiSelect from "./StageMultiSelect";
import { DateRangeReportFilterSelect } from "./DateRangeReportFilterSelect";
import RecencyReportFilterSelect from "./RecencyReportFilterSelect";

interface IReportTemplatePreviewFilterProps {
    templateType?: ReportTemplateTypes,
    idFilters: IdReportFilter[],
    setIdFilters: any,
    dateRangeFilters: DateRangeReportFilter[],
    setDateRangeFilters: any,
    recencyFilters: RecencyReportFilter[],
    setRecencyFilters: any,
    includeLotStatusFilter?: boolean
}

const ReportTemplatePreviewFilters: FC<IReportTemplatePreviewFilterProps> = ({
    templateType,
    idFilters,
    setIdFilters,
    dateRangeFilters,
    setDateRangeFilters,
    recencyFilters,
    setRecencyFilters,
}) => {

    const {data: projects} = useContext(ProjectsContext);

    const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
    const [selectedStageIds, setSelectedStageIds] = useState<number[]>([]);

    const handleProjectIdFilterChange = useCallback((projectId : number) => {
        if(!projectId || Number.isNaN(projectId)) {
            setSelectedProjectIds([projectId]);
            const newIdFilters = idFilters.filter(filter => 
                filter.filterTargetField != ReportFilterTargetFields.ProjectId
            );
    
            setIdFilters(newIdFilters);
        }
        else {
            setSelectedProjectIds([projectId]);
            const newIdFilters = idFilters.filter(filter => 
                filter.filterTargetField != ReportFilterTargetFields.ProjectId
            );
    
            setIdFilters([
                ...newIdFilters, 
                new IdReportFilter({
                    filterTargetField: ReportFilterTargetFields.ProjectId,
                    filterId: projectId
                })
            ]);
        }
    }, [idFilters, setIdFilters, setSelectedProjectIds]);

    const handleMultiProjectIdFilterChange = useCallback((projectIds : number[]) => {
        setSelectedProjectIds(projectIds);

        const newIdFilters = idFilters.filter(filter => 
            filter.filterTargetField != ReportFilterTargetFields.ProjectId
        );

        setIdFilters([
            ...newIdFilters, 
            ...(projectIds.map((x: number) => 
                new IdReportFilter({
                    filterTargetField: ReportFilterTargetFields.ProjectId,
                    filterId: x
                })
            ))
        ]);
    }, [idFilters, setIdFilters, setSelectedProjectIds]);

    const handleMultiStageIdFilterChange = useCallback((stageIds : number[]) => {
        setSelectedStageIds(stageIds);

        const newIdFilters = idFilters.filter(filter => 
            filter.filterTargetField != ReportFilterTargetFields.StageId
        );

        setIdFilters([
            ...newIdFilters, 
            ...(stageIds.map((x: number) => 
                new IdReportFilter({
                    filterTargetField: ReportFilterTargetFields.StageId,
                    filterId: x
                })
            ))
        ]);
    }, [idFilters, setIdFilters, setSelectedStageIds]);

    const getFinancialYear = useCallback((date: DateTime) => {
        if(date.month >= 7) {
            return date.year;
        }

        return date.year-1;
    }, []);


    const selectedProjectId = useMemo(() => (
        idFilters.filter(filter =>
            filter.filterTargetField == ReportFilterTargetFields.ProjectId
        )[0]?.filterId
    ), [idFilters]);

    const {data: minExchangeDate} = useMinExchangeDateByProjectId(selectedProjectId);

    const exchangeDateFinancialYearRange = useMemo(() => {
        if(!minExchangeDate) {
            return [getFinancialYear(DateTime.now())];
        }

        const financialYears = [];

        for(let i = getFinancialYear(DateTime.fromISO(minExchangeDate)); i <= getFinancialYear(DateTime.now()); i++) {
            financialYears.push(i);
        }

        return financialYears;
    }, [minExchangeDate, getFinancialYear]);

    const handleFinancialYearFilterChange = useCallback((event: any, targetField: ReportFilterTargetFields) => {
        const year = Number(event.target.value);
        setDateRangeFilters((oldFilters: DateRangeReportFilter[]) => {
            const existing = oldFilters.find(filter => filter.filterTargetField == targetField);
            const newFilters = [
                ...(
                    oldFilters.filter((filter: DateRangeReportFilter) => 
                        filter.filterTargetField != targetField
                    )
                ), 
                new DateRangeReportFilter({
                    ...existing,
                    filterTargetField: targetField,
                    isStartInclusive: existing?.isStartInclusive ?? true,
                    isEndInclusive: existing?.isEndInclusive ?? true,
                    startDate: `${year}-07-01`,
                    endDate: `${year + 1}-06-30`
                })
            ];

            return newFilters;
        })
    }, [setDateRangeFilters]);

    const handleSaleTypeChange = useCallback((event: any) => {
        setIdFilters((oldFilters: IdReportFilter[]) => [
            ...(oldFilters.filter(filter => filter.filterTargetField != ReportFilterTargetFields.CommissionTypeExchange)),
            new IdReportFilter({
                filterTargetField: ReportFilterTargetFields.CommissionTypeExchange,
                filterId: event.target.value
            })
        ]);
    }, [setIdFilters]);

    const handleLotStatusChange = useCallback((event: any) => {
        setIdFilters((oldFilters: IdReportFilter[]) => [
            ...(oldFilters.filter(filter => filter.filterTargetField != ReportFilterTargetFields.LotStatus)),
            new IdReportFilter({
                filterTargetField: ReportFilterTargetFields.LotStatus,
                filterId: event.target.value
            })
        ]);
    }, [setIdFilters]);

    useEffect(() => {
        switch(templateType) {
            case ReportTemplateTypes.LotSummary:
                handleFinancialYearFilterChange({target: {value: getFinancialYear(DateTime.now()) }}, ReportFilterTargetFields.ContractExchangeDate);
                handleFinancialYearFilterChange({target: {value: getFinancialYear(DateTime.now()) }}, ReportFilterTargetFields.ContractSettleDate);
                break;
            case ReportTemplateTypes.SalesSummary:
                handleFinancialYearFilterChange({target: {value: getFinancialYear(DateTime.now()) }}, ReportFilterTargetFields.ContractExchangeDate);
                break;
            case ReportTemplateTypes.ContractCancellationSummary:
                handleFinancialYearFilterChange({target: {value: getFinancialYear(DateTime.now()) }}, ReportFilterTargetFields.ContractCancellationDate);
                break;
        }

        if (templateType == ReportTemplateTypes.LotSummary ||
            templateType == ReportTemplateTypes.SalesSummary ||
            templateType == ReportTemplateTypes.ContractCancellationSummary) {
            return () => setDateRangeFilters(() => []);
        }

    }, [templateType, exchangeDateFinancialYearRange, handleFinancialYearFilterChange, getFinancialYear]);

    return (
        <>
            {
                templateType != ReportTemplateTypes.SalesAdviceToContract && 
                <Paper
                    sx={{
                        padding: 2
                    }}
                >
                    <Grid
                        container
                        gap={2}
                        direction={"row"}
                    >
                        {templateType == ReportTemplateTypes.LotsSettledByFinancialYear &&
                            <SettlementReportingPeriodSelect filters={dateRangeFilters} onChange={setDateRangeFilters} />}
                        {
                            (
                                templateType == ReportTemplateTypes.SalesSummary ||
                                templateType == ReportTemplateTypes.LotSummary ||
                                templateType == ReportTemplateTypes.ContractsOverdue ||
                                templateType == ReportTemplateTypes.CommissionReport ||
                                templateType == ReportTemplateTypes.GrossRealisationReport ||
                                templateType == ReportTemplateTypes.SalesRateReport ||
                                templateType == ReportTemplateTypes.UpcomingSettlements ||
                                templateType == ReportTemplateTypes.SettlementDateSummary ||
                                templateType == ReportTemplateTypes.ContractsExchangedSummary ||
                                templateType == ReportTemplateTypes.LotsSettledByFinancialYear ||
                                templateType == ReportTemplateTypes.StageLotCounts
                            ) &&
                            projects &&
                            <TextField
                                size={"small"}
                                sx={{
                                    minHeight: 56,
                                    minWidth: 250
                                }}
                                slotProps={{
                                    inputLabel: {
                                        sx: {
                                            top: 8,
                                            [`&.${inputLabelClasses.focused}`]: {
                                                top: 0
                                            }
                                        }
                                    },
                                    select: {
                                        sx: {
                                            minHeight: 56
                                        }
                                    }
                                }}
                                label={"Project"}
                                onChange={(event) => handleProjectIdFilterChange(+event.target.value)}
                                value={
                                        idFilters?.filter(filter => filter.filterTargetField == ReportFilterTargetFields.ProjectId)[0]?.filterId ?? -1}
                                select
                            >
                                <MenuItem key={-1} value={-1}><ListItemText primary="All" /></MenuItem>
                                {
                                    (projects.map(project => 
                                        <MenuItem key={project.id} value={project.id}><ListItemText primary={project.name} /></MenuItem>
                                    ))
                                }
                            </TextField>
                        }
                        {
                            templateType == ReportTemplateTypes.LotPricingComparison &&
                            projects &&
                            <TextField
                                size={"small"}
                                sx={{
                                    minHeight: 56,
                                    minWidth: 250
                                }}
                                slotProps={{
                                    inputLabel: {
                                        sx: {
                                            top: 8,
                                            [`&.${inputLabelClasses.focused}`]: {
                                                top: 0
                                            }
                                        }
                                    },
                                    select: {
                                        sx: {
                                            minHeight: 56
                                        },
                                        multiple: true,
                                        renderValue: (selected) => (
                                            <Box sx={{ display: 'flex', flexDirection: "column", flexWrap: 'wrap', gap: 0.5 }}>
                                                {
                                                    (selected as number[]).map((value) => (
                                                        <Box key={`project-filter-${value}`}>{projects.find(x => x.id == value)?.name}</Box>
                                                    ))
                                                }
                                            </Box>
                                        )
                                    }
                                }}
                                label={"Projects"}
                                onChange={(event) => handleMultiProjectIdFilterChange(event.target.value as unknown as number[])}
                                value={idFilters?.filter(filter => filter.filterTargetField == ReportFilterTargetFields.ProjectId).map(x => x.filterId)}
                                select
                            >
                                {
                                    (projects.map(project => 
                                        <MenuItem key={project.id} value={project.id}><ListItemText primary={project.name} /></MenuItem>
                                    ))
                                }
                            </TextField>
                        }
                        {
                            selectedProjectIds.length > 0 &&
                            templateType != ReportTemplateTypes.GrossRealisationReport &&
                            templateType != ReportTemplateTypes.LotsSettledByFinancialYear &&
                            <StageMultiSelect 
                                selectedProjectIds={selectedProjectIds} 
                                selectedStageIds={selectedStageIds} 
                                onChange={handleMultiStageIdFilterChange}
                                fullWidth={false}
                                GridProps={{
                                    size: undefined
                                }}
                                sx={{
                                    minHeight: 56,
                                    minWidth: 250
                                }}
                                slotProps={{
                                    inputLabel: {
                                        sx: {
                                            [`&.${inputLabelClasses.focused}`]: {
                                                top: 0
                                            }
                                        }
                                    },
                                    select: {
                                        sx: {
                                            minHeight: 56
                                        }
                                    }
                                }}
                            />
                        }
                        {
                            (
                                templateType == ReportTemplateTypes.SalesSummary ||
                                templateType == ReportTemplateTypes.LotSummary ||
                                templateType == ReportTemplateTypes.ContractCancellationSummary
                            ) &&
                            // idFilters.filter(filter => filter.filterTargetField == ReportFilterTargetFields.ProjectId).length > 0 &&
                            <TextField
                                sx={{
                                    height: 56,
                                    minWidth: 150
                                }}
                                slotProps={{
                                    select: {
                                        sx: {
                                            height: 56
                                        }
                                    }
                                }}
                                size={"small"}
                                label={"Financial Year"}
                                select
                                onChange={(event) => {
                                    switch(templateType) {
                                        case ReportTemplateTypes.SalesSummary:
                                            handleFinancialYearFilterChange(event, ReportFilterTargetFields.ContractExchangeDate);
                                            break;
                                        case ReportTemplateTypes.LotSummary:
                                            handleFinancialYearFilterChange(event, ReportFilterTargetFields.ContractExchangeDate);
                                            handleFinancialYearFilterChange(event, ReportFilterTargetFields.ContractSettleDate);
                                            break;
                                        case ReportTemplateTypes.ContractCancellationSummary:
                                            handleFinancialYearFilterChange(event, ReportFilterTargetFields.ContractCancellationDate);
                                    }
                                }}
                                value={
                                    getFinancialYear(
                                            DateTime.fromFormat(
                                                dateRangeFilters.filter(filter => {
                                                switch(templateType) {
                                                    case ReportTemplateTypes.LotSummary:
                                                    case ReportTemplateTypes.SalesSummary:
                                                        return filter.filterTargetField == ReportFilterTargetFields.ContractExchangeDate;
                                                    case ReportTemplateTypes.ContractCancellationSummary:
                                                        return filter.filterTargetField == ReportFilterTargetFields.ContractCancellationDate;
                                                }
                                            })[0]?.startDate ?? DateTime.now().toFormat('yyyy-MM-dd'), 
                                            'yyyy-MM-dd'
                                        )
                                    )
                                }
                            >
                                {
                                    exchangeDateFinancialYearRange?.map(year => (
                                        <MenuItem key={year} value={year}><ListItemText primary={year} /></MenuItem>
                                    ))
                                }
                            </TextField>
                        }
                        {
                            (templateType == ReportTemplateTypes.SalesSummary ||
                                templateType == ReportTemplateTypes.LotSummary ||
                                templateType == ReportTemplateTypes.ContractCancellationSummary) &&
                            <DateRangeReportFilterSelect
                                applicableFields={templateType == ReportTemplateTypes.ContractCancellationSummary
                                    ? [ReportFilterTargetFields.ContractCancellationDate]
                                    : templateType == ReportTemplateTypes.LotSummary
                                        ? [ReportFilterTargetFields.ContractExchangeDate, ReportFilterTargetFields.ContractSettleDate]
                                        : [ReportFilterTargetFields.ContractExchangeDate]}
                                dateRangeReportFilters={dateRangeFilters}
                                setDateRangeReportFilters={setDateRangeFilters}
                                readOnlyDates
                            />
                        }
                        {
                            (templateType == ReportTemplateTypes.SettlementDateSummary ||
                                templateType == ReportTemplateTypes.ContractsExchangedSummary) &&
                            <RecencyReportFilterSelect
                                applicableFields={templateType == ReportTemplateTypes.SettlementDateSummary
                                    ? [ReportFilterTargetFields.ContractSettleDate]
                                    : [ReportFilterTargetFields.ContractExchangeDate]}
                                recencyReportFilters={recencyFilters}
                                setRecencyReportFilters={setRecencyFilters}
                            />
                        }
                        {
                            (
                                templateType == ReportTemplateTypes.CommissionReport
                            ) &&
                            <DateRangeReportFilterSelect
                                applicableFields={[
                                    ReportFilterTargetFields.ContractExchangeDate,
                                    ReportFilterTargetFields.ContractSettleDate,
                                ]}
                                dateRangeReportFilters={dateRangeFilters}
                                setDateRangeReportFilters={setDateRangeFilters}
                            />
                        }
                        {
                            (
                                templateType == ReportTemplateTypes.GrossRealisationReport
                            ) &&
                            <TextField
                                sx={{
                                    height: 56,
                                    justifyContent: 'center',
                                    minWidth: 200
                                }}
                                slotProps={{
                                    select: {
                                        sx: {
                                            height: 56
                                        }
                                    }
                                }}
                                select
                                label={"Sales Type"}
                                defaultValue={""}
                                onChange={handleSaleTypeChange}
                            >
                                <MenuItem key={CommissionTypes.Exchange} value={CommissionTypes.Exchange}><ListItemText primary={"Exchange"} /></MenuItem>
                                <MenuItem key={CommissionTypes.Settlement} value={CommissionTypes.Settlement}><ListItemText primary={"Settlement"} /></MenuItem>
                            </TextField>
                        }
                        {
                            (
                                templateType == ReportTemplateTypes.Custom
                            ) &&
                            <TextField
                                sx={{
                                    height: 56,
                                    minWidth: 100
                                }}
                                slotProps={{
                                    select: {
                                        sx: {
                                            height: 56
                                        }
                                    }
                                }}
                                select
                                label={"Lot Sale Status"}
                                size={"small"}
                                onChange={handleLotStatusChange}
                            >
                                <MenuItem key={LotStatuses.Available} value={LotStatuses.Available}><ListItemText primary={"Available"} /></MenuItem>
                                <MenuItem key={LotStatuses.ComingSoon} value={LotStatuses.ComingSoon}><ListItemText primary={"Coming Soon"} /></MenuItem>
                                <MenuItem key={LotStatuses.ContractExchanged} value={LotStatuses.ContractExchanged}><ListItemText primary={"Contract Exchanged"} /></MenuItem>
                                <MenuItem key={LotStatuses.ContractIssued} value={LotStatuses.ContractIssued}><ListItemText primary={"Contract Issued"} /></MenuItem>
                                <MenuItem key={LotStatuses.ContractSettled} value={LotStatuses.ContractSettled}><ListItemText primary={"Contract Settled"} /></MenuItem>
                                <MenuItem key={LotStatuses.DisplayHome} value={LotStatuses.DisplayHome}><ListItemText primary={"Display Home"} /></MenuItem>
                                <MenuItem key={LotStatuses.Englobo} value={LotStatuses.Englobo}><ListItemText primary={"Englobo"} /></MenuItem>
                                <MenuItem key={LotStatuses.Future} value={LotStatuses.Future}><ListItemText primary={"Future"} /></MenuItem>
                                <MenuItem key={LotStatuses.OnHold} value={LotStatuses.OnHold}><ListItemText primary={"On Hold"} /></MenuItem>
                                <MenuItem key={LotStatuses.SalesAdviceIssued} value={LotStatuses.SalesAdviceIssued}><ListItemText primary={"Sales Advice Issued"} /></MenuItem>
                                <MenuItem key={LotStatuses.Unavailable} value={LotStatuses.Unavailable}><ListItemText primary={"Unavailable"} /></MenuItem>
                            </TextField>
                        }
                    </Grid>
                </Paper>
            }
        </>
    );
}

export default ReportTemplatePreviewFilters;
